import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Bedroom } from '../types/bedroom';
import { SingleBedIcon, DoubleBedIcon } from './BedIcons';

interface BedroomsSummaryProps {
  bedrooms: Bedroom[];
}

export const BedroomsSummary: React.FC<BedroomsSummaryProps> = ({ bedrooms }) => {
  const { t } = useTranslation();

  if (bedrooms.length === 0) {
    return null;
  }

  const totalBeds = bedrooms.reduce((sum, room) => sum + room.beds.length, 0);
  const totalSingle = bedrooms.reduce((sum, room) => sum + room.beds.filter((b) => b.type === 'single').length, 0);
  const totalDouble = bedrooms.reduce((sum, room) => sum + room.beds.filter((b) => b.type === 'double').length, 0);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={t('bedrooms.summaryRooms', { count: bedrooms.length })}
        />
        <Chip
          size="small"
          label={t('bedrooms.summaryBeds', { count: totalBeds })}
        />
        {totalSingle > 0 && (
          <Chip
            size="small"
            icon={<SingleBedIcon />}
            label={t('bedrooms.summarySingle', { count: totalSingle })}
          />
        )}
        {totalDouble > 0 && (
          <Chip
            size="small"
            icon={<DoubleBedIcon />}
            label={t('bedrooms.summaryDouble', { count: totalDouble })}
          />
        )}
      </Stack>

      <Stack spacing={1}>
        {bedrooms.map((bedroom, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
              {bedroom.name || t('bedrooms.bedroomDefault', { number: index + 1 })}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {bedroom.beds.map((bed, bedIndex) => (
                <Tooltip
                  key={bedIndex}
                  title={
                    bed.dimensions
                      ? `${t(`bedrooms.${bed.type}`)} — ${bed.dimensions} cm`
                      : t(`bedrooms.${bed.type}`)
                  }
                >
                  <Box
                    sx={{
                      color: bed.type === 'double' ? 'primary.main' : 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {bed.type === 'double' ? <DoubleBedIcon /> : <SingleBedIcon />}
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
